import type { IncentivePlan, IncentiveUnitRate, PerUnitTierPayout } from './types';

/**
 * The per-unit ladder, for the editor's worked examples and its checks before a save.
 *
 * ── THE SAME SUM AS THE PAYOUT ───────────────────────────────────────────────
 * This mirrors perUnitPay in the API's incentive.calc.ts line for line, so the
 * examples an admin reads while typing are what the payout will pay. The payout
 * view never calls it — it shows the engine's own figures.
 *
 *   Pays from 15.  Units 1–22 ₹50 · 23–30 ₹75 · 31 and above ₹150.
 *   14 → ₹0 · 15 → ₹750 · 23 → ₹1,175 · 35 → ₹2,450
 *
 * Below "pays from" nothing is paid. From it, EVERY unit is paid from unit 1, each
 * at the rate of the step it falls in (a marginal ladder, no cap on the last step),
 * and the total is rounded once at the end.
 */

/** Whole rupees, as the API rounds: once, at the end, never below 0. */
const roundRupees = (value: number): number =>
  !Number.isFinite(value) || value <= 0 ? 0 : Math.round(Number(value.toFixed(6)));

export const perUnitPay = (
  minUnits: number | null,
  rates: IncentiveUnitRate[],
  units: number,
): { amount: number; tiers: PerUnitTierPayout[]; reachedMinimum: boolean } => {
  const sorted = [...rates].sort((a, b) => a.fromUnit - b.fromUnit);
  const counted = Number.isFinite(units) && units > 0 ? units : 0;
  const reachedMinimum = counted > 0 && (minUnits === null || minUnits <= 0 || counted >= minUnits);
  const tiers = sorted.map((r, i): PerUnitTierPayout => {
    const toUnit = i + 1 < sorted.length ? sorted[i + 1].fromUnit - 1 : null;
    const top = toUnit === null ? counted : Math.min(counted, toUnit);
    const inStep = reachedMinimum ? Math.max(0, top - (r.fromUnit - 1)) : 0;
    return { fromUnit: r.fromUnit, toUnit, rate: r.rate, units: inStep, amount: inStep * r.rate };
  });
  const amount = reachedMinimum ? roundRupees(tiers.reduce((n, t) => n + t.amount, 0)) : 0;
  return { amount, tiers, reachedMinimum };
};

/** What savePerUnit sends. A blank box arrives here as NaN so the checks catch it. */
export interface PerUnitSettings {
  minUnits: number | null;
  allProducts: boolean;
  products: { productId: string; skuId: string | null }[];
  rates: IncentiveUnitRate[];
}

/** The server's words, so the admin reads the same sentence before saving as after. */
export const PER_UNIT_MESSAGES = {
  noRates: 'Add at least one rate.',
  firstRate: 'The first rate starts at unit 1 — use “Pays from” for a minimum.',
  rateSteps: 'Each rate starts at a whole number of units, higher than the one before.',
  rateAmount: 'A rate is a rupee amount of 0 or more, with at most 2 decimal places.',
  minUnits: '“Pays from” is a whole number of units, or blank.',
  products: 'Tick at least one product, or count all products.',
} as const;

/** The first thing the server would refuse in these settings, or null when it would take them. */
export const validatePerUnit = (s: PerUnitSettings): string | null => {
  if (s.rates.length === 0) return PER_UNIT_MESSAGES.noRates;
  if (s.rates.some((r) => !Number.isInteger(r.fromUnit) || r.fromUnit < 1)) return PER_UNIT_MESSAGES.rateSteps;
  const sorted = [...s.rates].sort((a, b) => a.fromUnit - b.fromUnit);
  if (sorted[0].fromUnit !== 1) return PER_UNIT_MESSAGES.firstRate;
  if (sorted.some((r, i) => i > 0 && r.fromUnit <= sorted[i - 1].fromUnit)) return PER_UNIT_MESSAGES.rateSteps;
  // Paise at most, as the server stores it — checked here so the refusal comes
  // before anything is saved, not after the plan's other settings already were.
  if (s.rates.some((r) => !Number.isFinite(r.rate) || r.rate < 0
    || Math.abs(Math.round(r.rate * 100) - r.rate * 100) > 1e-6)) return PER_UNIT_MESSAGES.rateAmount;
  if (s.minUnits !== null && (!Number.isInteger(s.minUnits) || s.minUnits < 1)) return PER_UNIT_MESSAGES.minUnits;
  if (!s.allProducts && s.products.length === 0) return PER_UNIT_MESSAGES.products;
  return null;
};

/**
 * The unit counts worth showing as examples: either side of "pays from" and of
 * each step's start, and a little past the last start. Counts below "pays from"
 * all pay ₹0, so only the one just under it is kept.
 */
export const perUnitExamples = (minUnits: number | null, rates: IncentiveUnitRate[]): number[] => {
  const starts = rates.map((r) => r.fromUnit).filter((n) => Number.isFinite(n));
  if (starts.length === 0) return [];
  const points = new Set<number>();
  if (minUnits !== null && minUnits > 0) {
    points.add(minUnits - 1);
    points.add(minUnits);
  }
  starts.forEach((s) => { points.add(s - 1); points.add(s); });
  points.add(Math.max(...starts) + 5);
  const floor = minUnits !== null && minUnits > 0 ? minUnits - 1 : 1;
  return [...points].filter((n) => n >= 1 && n >= floor).sort((a, b) => a - b);
};

const plural = (n: number, word: string): string => `${n} ${word}${n === 1 ? '' : 's'}`;

/** A per-unit plan in one line, for the plan list: "Per unit · pays from 15 · 3 rates · all products". */
export const describePerUnitPlan = (p: IncentivePlan): string => {
  const rateCount = p.unitRates?.length ?? 0;
  const allProducts = p.unitAllProducts ?? p.products.length === 0;
  return [
    'Per unit',
    p.unitMinUnits != null && p.unitMinUnits > 0 ? `pays from ${p.unitMinUnits}` : null,
    rateCount > 0 ? plural(rateCount, 'rate') : 'no rates yet',
    allProducts ? 'all products' : plural(p.products.length, 'product'),
    p.rules.length > 0 ? plural(p.rules.length, 'rule') : null,
  ].filter(Boolean).join(' · ');
};
